import { Dialog } from '@myosh/odin-components';
import { t } from 'i18next';
import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { useDeleteViewMutation, useUpdateViewMutation } from '../../../redux/config/views';
import Notifications from '../../common/notifications.component';
import useSyncView from '../hooks/use-sync-view';
import ViewNameEditor from '../view-components/view-name-editor';
import type { ManageViewsModalProps } from './view-handler.component';

const dialogId = 'view-manage-modal-dialog';
const toasterId = 'view-manage-modal-toaster';

export const ViewManageModal = ({ visible, onClose }: ManageViewsModalProps) => {
  const { viewOptions } = useSyncView();
  const [deleteView] = useDeleteViewMutation();
  const [updateView] = useUpdateViewMutation();

  const removeViewHandler = useCallback(
    (nameValue: string) => {
      deleteView(nameValue).then(() => toast.success(t('deleteViewSuccess'), { duration: 3000, toasterId: toasterId }));
    },
    [deleteView]
  );

  const updateViewHandler = useCallback(
    async ({ id, newName, existingName }: { id: string; newName: string; existingName: string }) => {
      if (newName.trim() === existingName?.trim()) {
        toast.error(t('updateViewExistingName'), { toasterId: toasterId });
        return;
      }
      const existingView = viewOptions.find((viewOption) => viewOption?.name?.trim() === newName?.trim());

      if (existingView) {
        toast.error(t('updateViewExistingName'), { toasterId: toasterId });
        return;
      }

      return updateView({
        id,
        name: newName,
      })
        .unwrap()
        .then(() => toast.success(t('updateViewSuccess'), { toasterId: toasterId }))
        .catch(() => {
          toast.error(t('updateViewFailure'), { toasterId: toasterId });
        });
    },
    [viewOptions, updateView]
  );

  return (
    <>
      {viewOptions && (
        <Dialog
          header={t('manageViews')}
          visible={visible}
          onClose={onClose}
          contentClassName="outline-hidden gap-1"
          id={dialogId}
          closeOnClickOutside={false}
        >
          {viewOptions?.map((item, index) => (
            <ViewNameEditor
              key={item.value}
              view={item}
              isEven={index % 2 === 0}
              onRemove={removeViewHandler}
              onNameUpdate={(viewName: string) =>
                updateViewHandler({ id: item.id, existingName: item.name!, newName: viewName })
              }
            />
          ))}
          {/* Use toasterId to target notifications to be dispalyed on top of dialog */}
          <Notifications appendTo={document.getElementById(dialogId) ?? document.body} toasterId={toasterId} />
        </Dialog>
      )}
    </>
  );
};
